import { useState, useCallback } from 'react';
import type { AnalysisType, ComplianceAnalysisResult } from '../../../shared/types';
import { buildAnalysisEmailHtml } from '../../services/emailHtml';
import { downloadAnalysisPdf, pdfFilenameFor } from '../../services/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import { copyRich, markdownToHtml, markdownToPlainText } from '../../utils/markdown';
import Modal from '../ui/Modal';

/* Barre d'actions (copier + PDF + email + WhatsApp + éditer) avec sa pop-up
 * email. Extrait tel quel de ResultsDisplay.tsx ; la modale email utilise
 * désormais la coquille partagée <Modal> (DOM identique). */

export default function ActionBar({ text, onEdit, result, analysisLabel, analysisType }: {
  text: string;
  onEdit?: () => void;
  result?: ComplianceAnalysisResult;
  /* La typologie habille les exports (teinte, silhouette, pilule d'en-tête) :
   * sans elle le rapport reste correct, mais neutre. */
  analysisLabel?: string;
  analysisType?: AnalysisType;
}) {
  const { features } = useAuth();
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Free plan: hide all sharing actions
  if (!features.copyText) return null;

  const handleCopy = useCallback(async () => {
    await copyRich(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  // Copie le rapport (HTML + texte) puis ouvre le client mail. Déclenché depuis
  // la pop-up explicative — l'utilisateur sait qu'il devra coller le contenu.
  const sendEmail = useCallback(async () => {
    const html = result ? buildAnalysisEmailHtml(result, { analysisLabel, analysisType }) : markdownToHtml(text);
    const plain = markdownToPlainText(text);
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
    } catch {
      await navigator.clipboard.writeText(plain);
    }
    setShowEmailModal(false);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 4000);
    window.open(`mailto:?subject=${encodeURIComponent('Analyse Visaly')}`);
  }, [text, result, analysisLabel, analysisType]);

  const handlePdf = useCallback(async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      await downloadAnalysisPdf(result, pdfFilenameFor(result.originalInput), { analysisLabel, analysisType });
    } finally {
      setPdfLoading(false);
    }
  }, [result, analysisLabel, analysisType]);

  const handleWhatsApp = useCallback(() => {
    window.open(`https://wa.me/?text=${encodeURIComponent('*Analyse Visaly*\n\n' + markdownToPlainText(text))}`, '_blank');
  }, [text]);

  const btn = "p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-wait";

  return (
    <div className="relative flex items-center justify-center gap-1 py-1.5">
      <button onClick={handleCopy} title={copied ? 'Copié !' : 'Copier'} className={btn}>
        {copied
          ? <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><polyline points="20 6 9 17 4 12" /></svg>
          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
      </button>
      {/* Téléchargement PDF — fichier prêt à joindre en pièce jointe */}
      {result && (
        <button onClick={handlePdf} disabled={pdfLoading} title="Télécharger en PDF" className={btn}>
          {pdfLoading
            ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" /></svg>
            : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>}
        </button>
      )}
      {/* Email — ouvre une pop-up explicative (le rapport se colle manuellement) */}
      <button onClick={() => setShowEmailModal(true)} title="Envoyer par email" className={btn}>
        {emailCopied
          ? <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><polyline points="20 6 9 17 4 12" /></svg>
          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>}
      </button>
      <button onClick={handleWhatsApp} title="WhatsApp" className={btn}>
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" /><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" /></svg>
      </button>
      {onEdit && (
        <button onClick={onEdit} title="Modifier" className={btn}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
        </button>
      )}
      {emailCopied && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs animate-fade-in">
          Rapport copié — collez-le (Ctrl+V) dans votre email
        </div>
      )}

      {showEmailModal && (
        <Modal
          onClose={() => setShowEmailModal(false)}
          className="w-full max-w-md rounded-2xl bg-cream-card border border-slate-200 p-6 text-left"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-violet-200 text-ink shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            </span>
            <h3 className="font-display text-lg text-slate-900">Envoyer le rapport par email</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Votre messagerie va s'ouvrir avec un nouveau message vide. Le rapport complet, mis en forme, est <strong>copié dans votre presse-papier</strong>.
          </p>
          <div className="rounded-lg bg-violet-50 border border-violet-100 px-4 py-3 mb-4">
            <p className="text-sm text-violet-800 leading-relaxed">
              Une fois le message ouvert, <strong>collez le rapport</strong> avec <kbd className="px-1.5 py-0.5 rounded bg-cream-app border border-violet-200 text-xs font-mono">Ctrl</kbd>+<kbd className="px-1.5 py-0.5 rounded bg-cream-app border border-violet-200 text-xs font-mono">V</kbd> (ou <kbd className="px-1.5 py-0.5 rounded bg-cream-app border border-violet-200 text-xs font-mono">Cmd</kbd>+<kbd className="px-1.5 py-0.5 rounded bg-cream-app border border-violet-200 text-xs font-mono">V</kbd> sur Mac), ou via <em>Édition ▸ Coller</em>.
            </p>
          </div>
          <p className="text-xs text-slate-400 mb-5">
            Astuce : pour envoyer le rapport en pièce jointe, utilisez plutôt le bouton « Télécharger en PDF ».
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowEmailModal(false)}
              className="btn-soft font-display px-4 py-2 text-sm"
            >
              Annuler
            </button>
            <button
              onClick={sendEmail}
              className="btn-ink font-display px-4 py-2 text-sm"
            >
              Ouvrir le client mail
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
